import { t } from '@lingui/core/macro'
import { removeAccents } from '@dolfin/business/utils'
import {
  Check,
  ChevronDown,
  PlusCircle,
  XCircle
} from '@untitled-ui/icons-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  ListBox,
  Select as AriaSelect,
  SelectProps as AriaSelectProps,
  SelectStateContext,
  SelectValue,
  ValidationResult
} from 'react-aria-components'
import { tv } from 'tailwind-variants'
import Badge, { BadgeColors, BadgeSizes } from './Badge'
import { Description, FieldError, Label } from './Field'
import { IconWrapper } from './IconWrapper'
import { DropdownItem, DropdownItemProps, dropdownItemStyles } from './ListBox'
import { Popover } from './Popover'
import SearchField from './SearchField'
import { composeTailwindRenderProps, groupBaseStyles } from './utils'

const styles = tv({
  extend: groupBaseStyles,
  base: 'h-[42px] gap-2 justify-between px-4 py-2'
})

export interface StickyBottomItemProps {
  label: string
  icon?: React.ReactNode
  onPress: () => void
}

type SelectRecord = {
  id?: string | number
  key?: string | number
  label?: string
  name?: string
}

type SelectItemKey = string | number

type StaticSelectItem = {
  itemKey: SelectItemKey
  id: string
  label: string
  element: React.ReactElement<DropdownItemProps>
}

type DynamicSelectItem<T extends SelectRecord> = {
  item: T
  itemKey: SelectItemKey
  id: string
  label: string
  element: React.ReactElement<DropdownItemProps>
}

type AriaSelectWithItemsProps<T extends object> = Omit<
  AriaSelectProps<T>,
  'children'
> & {
  items?: Iterable<T>
  children?: React.ReactNode
}

export interface SelectProps<T extends object>
  extends Omit<AriaSelectProps<T>, 'children'> {
  label?: string
  description?: string
  fallbackComponent?: React.ReactNode
  errorMessage?: string | ((validation: ValidationResult) => string)
  items?: Iterable<T>
  children: React.ReactNode | ((item: T) => React.ReactNode)
  iconLeading?: React.ReactNode
  iconLeadingIsPlaceholder?: boolean
  hideDescription?: boolean
  badgeChevronPosition?: 'inside' | 'outside' | 'hidden'
  badgeSize?: BadgeSizes
  enableClear?: boolean
  enableSearch?: boolean
  /** Shows an inline spinner and prevents opening the options menu. */
  isLoading?: boolean
  /**
   * Controls the visual treatment of the button that opens the options menu.
   * `compact` matches the single-badge trigger used by summary multi-selects.
   */
  openerType?: 'badge' | 'compact' | 'formField'
  badgeColor?: string
  stickyBottomItem?: StickyBottomItemProps
  renderSelectedValue?: (selectedKey: React.Key | null) => React.ReactNode
  onAddNew?: (
    value: string,
    inputRef: React.RefObject<HTMLInputElement | null>
  ) => string | void
}

const getItemLabel = (item: SelectRecord) =>
  item.label ?? item.name ?? String(item.id ?? '')

const getItemKey = (item: SelectRecord) => item.id ?? item.key

const normalizeSearchText = (value: string) =>
  removeAccents(value).toLowerCase().trim()

const searchTextEquals = (value: string, query: string) =>
  normalizeSearchText(value) === normalizeSearchText(query)

const searchTextIncludes = (value: string, query: string) =>
  normalizeSearchText(value).includes(normalizeSearchText(query))

const getSelectItemKey = (
  value: React.Key | null | undefined,
  fallback: string
): SelectItemKey => {
  if (typeof value === 'string' || typeof value === 'number') {
    return value
  }

  return fallback
}

const getStaticSelectItems = (children: React.ReactNode): StaticSelectItem[] =>
  React.Children.toArray(children).flatMap(child => {
    if (!React.isValidElement<DropdownItemProps>(child)) return []

    const label = child.props.label

    if (!label) return []

    const itemKey = getSelectItemKey(
      child.props.id != null ? child.props.id : child.key,
      label
    )

    return [
      {
        itemKey,
        id: String(itemKey),
        label,
        element: child
      }
    ]
  })

const getDropdownItemContent = (children: DropdownItemProps['children']) =>
  typeof children === 'function' ? undefined : children

const getDropdownItemElement = (
  node: React.ReactNode
): React.ReactElement<DropdownItemProps> | null =>
  React.isValidElement<DropdownItemProps>(node) ? node : null

function SelectClearButton({
  openerType
}: {
  openerType?: 'badge' | 'compact' | 'formField'
}) {
  const state = React.useContext(SelectStateContext)
  if (!state?.selectedKey) {
    return null
  }

  return (
    <Button
      slot={null}
      aria-label={t`Clear selection`}
      className={
        openerType === 'formField' || openerType === 'compact'
          ? 'absolute h-full right-10 top-0 outline-0'
          : 'top-0 outline-0'
      }
      onPress={() => {
        state?.setSelectedKey(null)
      }}>
      <IconWrapper width={18} Icon={XCircle} className={'text-slate-500'} />
    </Button>
  )
}

type BadgeSelectedValueProps = {
  defaultValue: string
  items: Array<{
    id?: string | number
    key?: string | number
    label?: string
    name?: string
  }>
}

function BadgeSelectedValue({ defaultValue, items }: BadgeSelectedValueProps) {
  const state = React.useContext(SelectStateContext)
  const item = items.find(item => getItemKey(item) === state?.selectedKey)

  return <p>{item?.label || item?.name || defaultValue}</p>
}

function CustomSelectedValue({
  renderSelectedValue
}: {
  renderSelectedValue: (selectedKey: React.Key | null) => React.ReactNode
}) {
  const state = React.useContext(SelectStateContext)

  return <>{renderSelectedValue(state?.selectedKey ?? null)}</>
}

interface LeadingAdornmentProps {
  adornment: React.ReactNode
  isPlaceholder?: boolean
}

function LeadingAdornment({ adornment, isPlaceholder }: LeadingAdornmentProps) {
  const state = React.useContext(SelectStateContext)
  return state?.selectedKey && isPlaceholder ? null : <>{adornment}</>
}

function Chevron({
  enableClear,
  openerType
}: {
  enableClear?: boolean
  openerType?: 'badge' | 'compact' | 'formField'
}) {
  const state = React.useContext(SelectStateContext)

  return (
    <ChevronDown
      aria-hidden
      className={`transition-transform group-open:rotate-180 w-4 h-4 text-neutral-500 group-disabled:text-neutral-200 focus:outline-0 m-0 p-0 shrink-0 ${
        openerType === 'formField' &&
        enableClear &&
        state?.selectedKey &&
        ' ml-6'
      } ${openerType === 'badge' && 'w-5 h-5 text-neutral-700'}`}
    />
  )
}

function InsideBadgeChevron() {
  return (
    <ChevronDown
      aria-hidden
      className={`transition-transform group-open:rotate-180 w-3 h-3 group-disabled:text-neutral-200 focus:outline-0 m-0 p-0 shrink-0`}
    />
  )
}

function SelectLoadingSkeleton() {
  return (
    <span aria-label={t`Loading options`} className='flex flex-1 items-center'>
      <span className='h-3 w-32 animate-pulse rounded-full bg-neutral-200' />
    </span>
  )
}

function StickyBottomItem({ label, icon, onPress }: StickyBottomItemProps) {
  const state = React.useContext(SelectStateContext)

  return (
    <div className='sticky bottom-0 border-t border-neutral-200 bg-white w-full'>
      <Button
        slot={null}
        className='w-full flex cursor-pointer select-none py-2.5 px-3.5 outline-0 bg-highlight-50 text-highlight-500 hover:bg-highlight-100 gap-3 items-center'
        onPress={() => {
          state?.close()
          onPress()
        }}>
        {icon && <span className='flex shrink-0'>{icon}</span>}
        <span className="text-base font-['Inter'] leading-tight">{label}</span>
      </Button>
    </div>
  )
}

function SelectMenuContent<T extends SelectRecord>({
  items,
  children,
  onAddNew,
  stickyBottomItem
}: {
  items?: Iterable<T>
  children: React.ReactNode | ((item: T) => React.ReactNode)
  onAddNew?: (
    value: string,
    inputRef: React.RefObject<HTMLInputElement | null>
  ) => string | void
  stickyBottomItem?: StickyBottomItemProps
}) {
  const state = React.useContext(SelectStateContext)
  const [filterText, setFilterText] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)
  const itemArray = useMemo(() => Array.from(items ?? []), [items])
  const staticItems = useMemo(
    () =>
      typeof children === 'function' ? [] : getStaticSelectItems(children),
    [children]
  )
  const dynamicItems = useMemo(() => {
    if (typeof children !== 'function') return []

    return itemArray.flatMap(item => {
      const element = getDropdownItemElement(children(item))

      if (!element) return []

      const { id } = element.props
      const itemKeyValue = getItemKey(item)
      const itemId =
        id != null
          ? String(id)
          : itemKeyValue != null
            ? String(itemKeyValue)
            : element.key != null
              ? String(element.key)
              : getItemLabel(item)
      const itemKey = getSelectItemKey(
        id != null ? id : itemKeyValue != null ? itemKeyValue : element.key,
        getItemLabel(item)
      )

      return [
        {
          item,
          itemKey,
          id: itemId,
          label: String(element.props.label ?? getItemLabel(item)),
          element
        }
      ]
    })
  }, [children, itemArray])
  const trimmedFilterText = filterText.trim()
  const normalizedFilterText = normalizeSearchText(trimmedFilterText)
  const hasStaticChildren = typeof children !== 'function'
  const hasFilterableStaticChildren =
    hasStaticChildren && staticItems.length > 0
  const searchableItems = hasStaticChildren ? staticItems : dynamicItems
  const hasExactMatch = searchableItems.some(item =>
    searchTextEquals(item.label, normalizedFilterText)
  )
  const showAddNewOption =
    Boolean(onAddNew) && trimmedFilterText !== '' && !hasExactMatch
  const selectedKey =
    state?.selectedKey == null ? null : String(state.selectedKey)
  const filteredDynamicItems = useMemo(() => {
    if (!normalizedFilterText) return dynamicItems

    return dynamicItems.filter(item =>
      searchTextIncludes(item.label, normalizedFilterText)
    )
  }, [dynamicItems, normalizedFilterText])
  const filteredStaticItems = useMemo(() => {
    if (!normalizedFilterText) return staticItems

    return staticItems.filter(item =>
      searchTextIncludes(item.label, normalizedFilterText)
    )
  }, [normalizedFilterText, staticItems])
  const visibleItemsCount = hasStaticChildren
    ? hasFilterableStaticChildren
      ? filteredStaticItems.length
      : React.Children.count(children)
    : filteredDynamicItems.length
  const showNoResults =
    trimmedFilterText !== '' &&
    visibleItemsCount === 0 &&
    !showAddNewOption &&
    (!hasStaticChildren || hasFilterableStaticChildren)

  useEffect(() => {
    if (state?.isOpen) {
      searchInputRef.current?.focus()
    }
  }, [state?.isOpen])

  const selectAndClose = (key: SelectItemKey) => {
    state?.setSelectedKey(key)
    state?.close()
  }

  const handleAddNew = () => {
    if (!showAddNewOption) return

    const nextKey =
      onAddNew?.(trimmedFilterText, searchInputRef) ??
      trimmedFilterText.toLowerCase()
    state?.setSelectedKey(nextKey)
    setFilterText('')
    state?.close()
  }

  const handleSearchChange = (value: string) => {
    setFilterText(value)

    requestAnimationFrame(() => {
      searchInputRef.current?.focus()
    })
  }

  const renderMenuItem = ({
    itemKey,
    id,
    label,
    content,
    description,
    leadingAdornment,
    trailingAdornment,
    isSuggested
  }: {
    itemKey: SelectItemKey
    id: string
    label: string
    content?: React.ReactNode
    description?: string
    leadingAdornment?: React.ReactNode
    trailingAdornment?: React.ReactNode
    isSuggested?: boolean
  }) => {
    const isSelected = selectedKey === id

    return (
      <button
        key={id}
        type='button'
        role='option'
        aria-selected={isSelected}
        onClick={() => selectAndClose(itemKey)}
        className={dropdownItemStyles({})}>
        <div className='flex group flex-row w-full items-center justify-between gap-3'>
          {leadingAdornment && (
            <div className='flex shrink-0 justify-center items-center'>
              {leadingAdornment}
            </div>
          )}
          {content != null ? (
            <div className='min-w-0 grow'>{content}</div>
          ) : (
            <div className='flex flex-col gap-0.5 w-full label-block items-start justify-start grow'>
              <span
                className={`block text-base font-['Inter'] leading-tight label ${
                  description ? 'shrink-0' : 'truncate ...'
                }`}>
                {label}
              </span>
              {description && (
                <span className="block text-xs font-medium font-['Plus Jakarta Sans'] leading-tight label-description truncate text-neutral-500 ...">
                  {description}
                </span>
              )}
            </div>
          )}
          {trailingAdornment && (
            <div className='flex shrink-0 pl-4'>{trailingAdornment}</div>
          )}
          {!trailingAdornment && isSuggested && (
            <div className='text-xs text-success-500 flex shrink-0'>
              {t`Suggested`}
            </div>
          )}
          <div className='flex shrink-0 justify-center items-center'>
            <Check
              width={18}
              className={isSelected ? 'text-highlight-600' : 'text-transparent'}
            />
          </div>
        </div>
      </button>
    )
  }

  const renderStaticItem = (item: StaticSelectItem) => {
    const { description, isSuggested, leadingAdornment, trailingAdornment } =
      item.element.props

    return renderMenuItem({
      itemKey: item.itemKey,
      id: item.id,
      label: item.label,
      content: getDropdownItemContent(item.element.props.children),
      description,
      leadingAdornment,
      trailingAdornment,
      isSuggested
    })
  }

  const renderDynamicItem = (item: DynamicSelectItem<T>) => {
    const { element } = item
    const {
      description,
      isSuggested,
      leadingAdornment,
      trailingAdornment,
      label
    } = element.props

    return renderMenuItem({
      itemKey: item.itemKey,
      id: item.id,
      label: String(label ?? item.label),
      content: getDropdownItemContent(element.props.children),
      description,
      leadingAdornment,
      trailingAdornment,
      isSuggested
    })
  }

  const renderAddNewButton = () => (
    <button
      type='button'
      role='option'
      aria-selected='false'
      onClick={handleAddNew}
      className={dropdownItemStyles({})}>
      <div className='flex group flex-row w-full items-center justify-between gap-3'>
        <div className='flex shrink-0 justify-center items-center'>
          <PlusCircle aria-hidden className='h-4 w-4 text-neutral-500' />
        </div>
        <div className='flex flex-col gap-0.5 w-full label-block items-start justify-start grow'>
          <span className="block text-base font-['Inter'] leading-tight label truncate ...">
            {t`Add "${trimmedFilterText}"`}
          </span>
        </div>
      </div>
    </button>
  )

  return (
    <>
      <div className='border-b w-full border-neutral-200 px-3.5 py-2'>
        <SearchField
          aria-label={t`Search options`}
          placeholder={t`Search`}
          inputRef={searchInputRef}
          isBorderless
          hideSearchIcon
          value={filterText}
          onChange={handleSearchChange}
          onKeyDown={event => event.stopPropagation()}
        />
      </div>
      {hasStaticChildren &&
      (trimmedFilterText === '' || !hasFilterableStaticChildren) &&
      !showAddNewOption ? (
        <ListBox
          className={`w-full outline-none overflow-auto [clip-path:inset(0_0_0_0_round_.4rem)] ${stickyBottomItem ? 'max-h-[200px]' : 'max-h-[inherit]'}`}>
          {children}
        </ListBox>
      ) : (
        <div
          role='listbox'
          aria-label='Options'
          className={`w-full outline-none overflow-auto [clip-path:inset(0_0_0_0_round_.4rem)] ${stickyBottomItem ? 'max-h-[200px]' : 'max-h-[inherit]'}`}>
          {hasFilterableStaticChildren
            ? filteredStaticItems.map(item => renderStaticItem(item))
            : typeof children === 'function' && (
                <>{filteredDynamicItems.map(item => renderDynamicItem(item))}</>
              )}
          {showAddNewOption && renderAddNewButton()}
          {showNoResults && (
            <div className='px-3.5 py-2.5 text-sm text-neutral-500'>
              {t`No results found`}
            </div>
          )}
        </div>
      )}
      {stickyBottomItem && <StickyBottomItem {...stickyBottomItem} />}
    </>
  )
}

function Select<T extends object>({
  label,
  description,
  errorMessage,
  children,
  items,
  iconLeading,
  enableClear,
  iconLeadingIsPlaceholder,
  hideDescription,
  fallbackComponent,
  badgeChevronPosition = 'outside',
  openerType = 'formField',
  badgeColor = 'neutral',
  badgeSize = BadgeSizes.s,
  stickyBottomItem,
  renderSelectedValue,
  onAddNew,
  enableSearch: _enableSearch,
  isLoading = false,
  ...props
}: SelectProps<T>) {
  const isInteractionDisabled = props.isDisabled || isLoading

  return React.createElement(
    AriaSelect as React.ComponentType<AriaSelectWithItemsProps<T>>,
    {
      ...props,
      items,
      isDisabled: isInteractionDisabled,
      'aria-label': label ?? description ?? props.placeholder ?? 'Select',
      className: composeTailwindRenderProps(
        props.className,
        'group flex flex-col gap-1 relative text-left'
      )
    },
    <>
      {label && <Label className={'capitalize-first-letter'}>{label}</Label>}
      {Array.isArray(items) &&
        items.length === 0 &&
        !children &&
        fallbackComponent}
      {((Array.isArray(items) && items.length > 0) || !!children) && (
        <div className={'relative flex flex-col'}>
          <Button
            aria-busy={isLoading}
            className={
              openerType === 'formField'
                ? styles
                : openerType === 'compact'
                  ? 'flex h-9 max-w-full items-center justify-between gap-2 rounded-full border border-neutral-300 px-3 text-sm text-neutral-700 outline-none transition-colors hover:border-neutral-400 focus-visible:ring-2 focus-visible:ring-primary-500' +
                    ' w-full' +
                    (isInteractionDisabled ? ' opacity-50' : '')
                  : 'outline-0 focus:outline-0 flex flex-row gap-2 w-fit justify-center items-center' +
                    (isInteractionDisabled ? ' opacity-50' : '')
            }>
            {isLoading ? (
              <>
                <SelectLoadingSkeleton />
                {(openerType === 'formField' ||
                  openerType === 'compact' ||
                  (openerType === 'badge' &&
                    badgeChevronPosition === 'outside')) && (
                  <Chevron enableClear={enableClear} openerType={openerType} />
                )}
              </>
            ) : (
              <>
                <div className={'flex items-center gap-3 grow truncate'}>
                  {openerType === 'formField' && (
                    <>
                      <LeadingAdornment
                        adornment={iconLeading}
                        isPlaceholder={iconLeadingIsPlaceholder}
                      />
                      {renderSelectedValue ? (
                        <CustomSelectedValue
                          renderSelectedValue={renderSelectedValue}
                        />
                      ) : (
                        <SelectValue
                          className={`block truncate ... [&_span]:block items-center text-sm focus:outline-0 [&_.label-block]:flex-row [&_.label-block]:gap-2 [&_.label-block]:items-center [&_.suggested-indicator]:hidden ${
                            hideDescription && '[&_.label-description]:hidden'
                          }`}
                        />
                      )}
                    </>
                  )}
                  {openerType === 'compact' && (
                    <SelectValue className='block truncate text-sm focus:outline-0 [&_span]:block [&_.label-block]:flex-row [&_.label-block]:gap-2 [&_.label-block]:items-center [&_.suggested-indicator]:hidden' />
                  )}
                  {openerType === 'badge' && (
                    <Badge
                      size={badgeSize}
                      color={
                        badgeColor === 'auto'
                          ? undefined
                          : (badgeColor as BadgeColors)
                      }
                      iconTrailing={
                        badgeChevronPosition === 'inside' && (
                          <InsideBadgeChevron />
                        )
                      }>
                      <div
                        className={
                          'flex flex-row items-center justify-center gap-2'
                        }>
                        <BadgeSelectedValue
                          defaultValue={props.placeholder || t`Select`}
                          items={
                            Array.from(items ?? []) as Array<{
                              id?: string | number
                              key?: string | number
                              label?: string
                              name?: string
                            }>
                          }
                        />
                        {enableClear && (
                          <SelectClearButton openerType={openerType} />
                        )}
                      </div>
                    </Badge>
                  )}
                </div>
                {(openerType === 'formField' ||
                  openerType === 'compact' ||
                  (openerType === 'badge' &&
                    badgeChevronPosition === 'outside')) && (
                  <Chevron enableClear={enableClear} openerType={openerType} />
                )}
              </>
            )}
          </Button>
          {enableClear &&
            (openerType === 'formField' || openerType === 'compact') && (
              <SelectClearButton openerType={openerType} />
            )}
        </div>
      )}
      {description && <Description>{description}</Description>}
      <FieldError>{errorMessage}</FieldError>
      <Popover className='min-w-[--trigger-width] scrollbar-hide'>
        {typeof children === 'function' && (
          <ListBox
            items={items}
            className='hidden'
            aria-label='Hidden options collection'>
            {children}
          </ListBox>
        )}
        <SelectMenuContent
          items={items as Iterable<SelectRecord> | undefined}
          onAddNew={onAddNew}
          stickyBottomItem={stickyBottomItem}>
          {
            children as
              | React.ReactNode
              | ((item: SelectRecord) => React.ReactNode)
          }
        </SelectMenuContent>
      </Popover>
    </>
  )
}

export function SelectItem(props: DropdownItemProps) {
  return <DropdownItem {...props} />
}

export default Select
